import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '@auth/AuthProvider';
import TrainingController from '@data/controller/TrainingController';
import { TrainingVolunteerAssignmentBundle } from '@interfaces/training.interface';
import i18next from 'i18next';

// Every Training Module assigned to a person, across all of the teams they
// volunteer on.
export default function useVolunteerTrainingAssignments(personID?: string) {
  const auth = useAuth();
  const churchID = auth.churchId;

  const [assignments, setAssignments] = useState<TrainingVolunteerAssignmentBundle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!churchID || !personID) return;

    setLoading(true);
    setError(null);

    const response = await TrainingController.fetchVolunteerTrainingAssignments(churchID, personID, auth);
    const results = response.body?.results;

    if (response.error || !results) {
      setError(response.error || i18next.t('volunteering:errors.loadAssignedTraining'));
      setAssignments([]);
    } else {
      setAssignments(results);
    }

    setLoading(false);
    setHasLoaded(true);
  }, [churchID, personID, auth]);

  // A just-created assignment shows immediately, rather than waiting on the
  // refetch behind it. Re-assigning a Module replaces its row instead of
  // doubling it.
  const addAssignment = useCallback((bundle: TrainingVolunteerAssignmentBundle) => {
    setAssignments((currentAssignments) => [
      ...currentAssignments.filter((existing) => existing.assignment.moduleID !== bundle.assignment.moduleID),
      bundle
    ]);
  }, []);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  return { assignments, loading, hasLoaded, error, refetch: fetch, addAssignment } as const;
}
